import React, { useCallback, useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { X, Phone, RefreshCw } from 'lucide-react';
import { fastapiService } from '../../services/fastapiService';

const ROWS_PER_PAGE = 50;

function formatDuration(seconds: number | null | undefined) {
  if (seconds == null || seconds < 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function getAnalysis(r: Record<string, unknown>) {
  const ar = r?.analysis_result;
  if (typeof ar === 'string') {
    try {
      return JSON.parse(ar) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return (ar as Record<string, unknown>) || {};
}

type TranslationTurn = { speaker?: string; text?: string };

function turnsToTranscriptText(turns: TranslationTurn[]): string {
  return turns
    .map((t) => `${t.speaker || 'Speaker'}: ${t.text || ''}`.trim())
    .filter(Boolean)
    .join('\n');
}

/**
 * Unifies Retell-style analysis_result (call_summary, transcript, user_sentiment, …)
 * with the human-call pipeline shape from /call-analysis/analyze (translation[], call_outcome, …).
 */
function normalizeCallAnalysis(r: Record<string, unknown>) {
  const raw = getAnalysis(r);

  let transcriptText = '';
  if (typeof raw.transcript === 'string' && raw.transcript.trim()) {
    transcriptText = raw.transcript.trim();
  } else if (Array.isArray(raw.translation)) {
    transcriptText = turnsToTranscriptText(raw.translation as TranslationTurn[]);
  } else if (Array.isArray(raw.normalized_transcript)) {
    transcriptText = turnsToTranscriptText(raw.normalized_transcript as TranslationTurn[]);
  }
  if (!transcriptText) {
    const merged = r.merged_utterances;
    if (Array.isArray(merged) && merged.length > 0) {
      transcriptText = turnsToTranscriptText(merged as TranslationTurn[]);
    }
  }

  let summaryText = '';
  if (typeof raw.call_summary === 'string' && raw.call_summary.trim()) {
    summaryText = raw.call_summary.trim();
  } else {
    const bits: string[] = [];
    if (typeof raw.call_intent === 'string' && raw.call_intent.trim()) {
      bits.push(`Intent: ${raw.call_intent.trim()}`);
    }
    if (typeof raw.call_outcome === 'string' && raw.call_outcome.trim()) {
      bits.push(`Outcome: ${raw.call_outcome.trim()}`);
    }
    if (Array.isArray(raw.key_topics) && raw.key_topics.length) {
      const topics = (raw.key_topics as unknown[]).map((x) => String(x)).filter(Boolean);
      if (topics.length) bits.push(`Topics: ${topics.join(', ')}`);
    }
    summaryText = bits.join('\n\n');
  }

  const userSentimentLabel =
    typeof raw.user_sentiment === 'string' && String(raw.user_sentiment).trim()
      ? String(raw.user_sentiment).trim()
      : undefined;

  const callQualityLabel =
    typeof raw.call_quality === 'string' && raw.call_quality.trim()
      ? raw.call_quality.trim()
      : undefined;

  const custom = raw.custom_analysis_data as Record<string, unknown> | undefined;
  const endReason =
    custom?.disconnection_reason != null && String(custom.disconnection_reason).trim()
      ? String(custom.disconnection_reason)
      : '—';

  return {
    raw,
    transcriptText,
    summaryText,
    userSentimentLabel,
    callQualityLabel,
    callSuccessful: raw.call_successful as boolean | undefined,
    outcomeText: typeof raw.call_outcome === 'string' ? raw.call_outcome.trim() : '',
    suggestedFollowup:
      typeof raw.suggested_followup_message === 'string' ? raw.suggested_followup_message.trim() : '',
    callerImprovement:
      typeof raw.caller_improvement_scope === 'string' ? raw.caller_improvement_scope.trim() : '',
    whatBetter:
      typeof raw.what_could_have_been_better === 'string' ? raw.what_could_have_been_better.trim() : '',
    endReason,
  };
}

function SentimentBadge({ value }: { value?: string }) {
  const v = (value || '').toLowerCase();
  const colors: Record<string, string> = {
    positive: 'bg-green-100 text-green-800',
    neutral: 'bg-gray-100 text-gray-700',
    negative: 'bg-red-100 text-red-700',
  };
  const c = colors[v] || 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${c}`}>
      {value || '—'}
    </span>
  );
}

interface WorkspaceCallLogsModalProps {
  workspaceId: string;
  workspaceName: string;
  isOpen: boolean;
  onClose: () => void;
}

export const WorkspaceCallLogsModal: React.FC<WorkspaceCallLogsModalProps> = ({
  workspaceId,
  workspaceName,
  isOpen,
  onClose,
}) => {
  const [recordings, setRecordings] = useState<Record<string, unknown>[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Record<string, unknown> | null>(null);
  const [page, setPage] = useState(1);
  const [directionFilter, setDirectionFilter] = useState('');

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    setError('');
    try {
      const res = (await fastapiService.getWorkspaceCallRecordings(workspaceId, {
        limit: ROWS_PER_PAGE,
        offset: (page - 1) * ROWS_PER_PAGE,
        direction: directionFilter || undefined,
      })) as { success?: boolean; data?: Record<string, unknown>[]; total?: number };
      if (res.success !== false) {
        setRecordings(Array.isArray(res.data) ? res.data : []);
        setTotal(typeof res.total === 'number' ? res.total : 0);
      } else {
        setError('Failed to load call logs');
        setRecordings([]);
      }
    } catch (e: unknown) {
      const err = e as { error?: string; message?: string };
      setError(err?.error || err?.message || 'Failed to load call logs');
      setRecordings([]);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, page, directionFilter]);

  useEffect(() => {
    if (isOpen) {
      load();
    }
  }, [isOpen, load]);

  const totalPages = Math.max(1, Math.ceil(total / ROWS_PER_PAGE));
  const selectedNorm = selected ? normalizeCallAnalysis(selected) : null;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-admin-200 bg-white shadow-xl">
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-admin-200 px-4 py-3 sm:px-5">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
              <Phone className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2 className="truncate text-lg font-semibold text-admin-900">Call history</h2>
              <p className="truncate text-xs text-admin-500">
                {workspaceName} · {total} sessions
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <select
              value={directionFilter}
              onChange={(e) => {
                setDirectionFilter(e.target.value);
                setPage(1);
              }}
              className="rounded-lg border border-admin-200 bg-white px-2 py-1.5 text-xs text-admin-800"
            >
              <option value="">All directions</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
            <button
              type="button"
              onClick={() => load()}
              disabled={loading}
              className="inline-flex items-center gap-1 rounded-lg border border-admin-200 px-2.5 py-1.5 text-xs font-medium text-admin-700 hover:bg-admin-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-admin-500 hover:bg-admin-100 hover:text-admin-800"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {error && <div className="shrink-0 border-b border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700">{error}</div>}

        <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
          <div className="min-h-[240px] flex-1 overflow-auto lg:min-h-0">
            {loading && recordings.length === 0 ? (
              <div className="flex items-center justify-center py-16">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-admin-200 border-t-blue-600" />
              </div>
            ) : (
              <table className="min-w-full divide-y divide-admin-100 text-sm">
                <thead className="sticky top-0 z-[1] bg-admin-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-admin-500">
                      Time
                    </th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-admin-500">
                      Dur
                    </th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-admin-500">
                      Dir
                    </th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-admin-500">
                      From
                    </th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-admin-500">
                      To
                    </th>
                    <th className="hidden px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-admin-500 md:table-cell">
                      Session ID
                    </th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-admin-500">
                      User sentiment
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-admin-100 bg-white">
                  {recordings.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-admin-500">
                        No call recordings found
                      </td>
                    </tr>
                  ) : (
                    recordings.map((r, idx) => {
                      const id = String(r.id ?? r.call_id ?? `row-${idx}`);
                      const na = normalizeCallAnalysis(r);
                      const sentiment = na.userSentimentLabel;
                      const started = r.call_started_at as string | undefined;
                      const isSel = selected && String(selected.id ?? selected.call_id) === String(r.id ?? r.call_id);
                      return (
                        <tr
                          key={id}
                          onClick={() => setSelected(r)}
                          className={`cursor-pointer transition-colors hover:bg-blue-50/80 ${isSel ? 'bg-blue-50' : ''}`}
                        >
                          <td className="whitespace-nowrap px-3 py-2 text-admin-900">
                            {started
                              ? format(parseISO(String(started).replace('Z', '')), 'yyyy-MM-dd HH:mm')
                              : '—'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-admin-700">
                            {formatDuration(r.duration as number | undefined)}
                          </td>
                          <td className="px-3 py-2 capitalize text-admin-700">{String(r.direction || '—')}</td>
                          <td className="max-w-[100px] truncate px-3 py-2 text-admin-700" title={String(r.from_number || '')}>
                            {String(r.from_number || '—')}
                          </td>
                          <td className="max-w-[100px] truncate px-3 py-2 text-admin-700" title={String(r.to_number || '')}>
                            {String(r.to_number || '—')}
                          </td>
                          <td className="hidden max-w-[120px] truncate px-3 py-2 font-mono text-xs text-admin-600 md:table-cell" title={String(r.call_id || '')}>
                            {String(r.call_id || '—')}
                          </td>
                          <td className="px-3 py-2">
                            <SentimentBadge value={sentiment} />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </div>

          {selected && (
            <div className="flex w-full shrink-0 flex-col border-t border-admin-200 bg-admin-50/50 lg:w-[380px] lg:border-l lg:border-t-0">
              <div className="flex items-center justify-between border-b border-admin-200 px-4 py-3">
                <h3 className="text-sm font-semibold text-admin-900">Call details</h3>
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="rounded p-1 text-admin-500 hover:bg-admin-200/60 lg:hidden"
                  aria-label="Close details"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="max-h-[50vh] space-y-4 overflow-y-auto p-4 lg:max-h-none lg:flex-1">
                <div className="rounded-lg border border-admin-200 bg-white p-3 text-sm">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-admin-500">Identifiers</p>
                  <dl className="mt-2 space-y-1.5">
                    <div className="flex justify-between gap-2">
                      <dt className="text-admin-500">Call ID</dt>
                      <dd className="break-all text-right font-mono text-xs text-admin-900">
                        {String(selected.call_id || '—')}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-admin-500">From → To</dt>
                      <dd className="text-right text-admin-800">
                        {String(selected.from_number || '—')} → {String(selected.to_number || '—')}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-2">
                      <dt className="text-admin-500">Duration</dt>
                      <dd className="font-medium text-admin-900">{formatDuration(selected.duration as number | undefined)}</dd>
                    </div>
                  </dl>
                </div>
                {typeof selected.call_audio_url === 'string' && selected.call_audio_url.trim() ? (
                  <div className="rounded-lg border border-admin-200 bg-white p-3">
                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-admin-500">Recording</p>
                    <audio controls className="h-9 w-full" src={selected.call_audio_url}>
                      Your browser does not support audio.
                    </audio>
                  </div>
                ) : null}
                {selectedNorm && (
                  <>
                    <div className="rounded-lg border border-admin-200 bg-white p-3 text-sm">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-admin-500">Analysis</p>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <div className="col-span-2">
                          <span className="text-xs text-admin-500">Outcome</span>
                          <div className="mt-0.5 space-y-1">
                            {selectedNorm.callSuccessful === true ? (
                              <span className="text-green-600">Successful</span>
                            ) : null}
                            {selectedNorm.callSuccessful === false ? (
                              <span className="text-red-600">Unsuccessful</span>
                            ) : null}
                            {selectedNorm.outcomeText ? (
                              <p className="leading-snug text-admin-800">{selectedNorm.outcomeText}</p>
                            ) : null}
                            {selectedNorm.callSuccessful === undefined && !selectedNorm.outcomeText ? (
                              <span className="text-admin-400">—</span>
                            ) : null}
                          </div>
                        </div>
                        <div className="col-span-2 sm:col-span-1">
                          <span className="text-xs text-admin-500">User sentiment</span>
                          <div className="mt-0.5">
                            <SentimentBadge value={selectedNorm.userSentimentLabel} />
                          </div>
                        </div>
                        <div className="col-span-2 sm:col-span-1">
                          <span className="text-xs text-admin-500">Call quality</span>
                          <p className="mt-0.5 text-sm text-admin-800">{selectedNorm.callQualityLabel || '—'}</p>
                        </div>
                        <div className="col-span-2">
                          <span className="text-xs text-admin-500">End reason</span>
                          <p className="mt-0.5 text-admin-800">{selectedNorm.endReason}</p>
                        </div>
                      </div>
                      {selectedNorm.suggestedFollowup ? (
                        <div className="mt-3 border-t border-admin-100 pt-3">
                          <span className="text-xs text-admin-500">Suggested follow-up</span>
                          <p className="mt-1 text-sm text-admin-800">{selectedNorm.suggestedFollowup}</p>
                        </div>
                      ) : null}
                      {selectedNorm.callerImprovement ? (
                        <div className="mt-3 border-t border-admin-100 pt-3">
                          <span className="text-xs text-admin-500">Caller improvement</span>
                          <p className="mt-1 text-sm text-admin-800">{selectedNorm.callerImprovement}</p>
                        </div>
                      ) : null}
                      {selectedNorm.whatBetter ? (
                        <div className="mt-3 border-t border-admin-100 pt-3">
                          <span className="text-xs text-admin-500">What could have been better</span>
                          <p className="mt-1 text-sm text-admin-800">{selectedNorm.whatBetter}</p>
                        </div>
                      ) : null}
                    </div>
                    {selectedNorm.summaryText ? (
                      <div className="rounded-lg border border-admin-200 bg-white p-3">
                        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-admin-500">Summary</p>
                        <p className="whitespace-pre-wrap text-sm leading-relaxed text-admin-800">
                          {selectedNorm.summaryText}
                        </p>
                      </div>
                    ) : null}
                    {selectedNorm.transcriptText ? (
                      <div className="rounded-lg border border-admin-200 bg-white p-3">
                        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-admin-500">Transcript</p>
                        <pre className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-md border border-admin-100 bg-admin-50/50 p-2 text-xs text-admin-800">
                          {selectedNorm.transcriptText}
                        </pre>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {!loading && recordings.length > 0 && (
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-admin-200 bg-admin-50/80 px-4 py-2.5 text-xs text-admin-600">
            <span>
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-lg border border-admin-200 bg-white px-3 py-1 font-medium disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border border-admin-200 bg-white px-3 py-1 font-medium disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
